/**
 * Voice channel statuses (2026-11-01, Discord's): one short text per voice
 * channel, from READY and GUILD_CREATE (on the channels) and every
 * VOICE_CHANNEL_STATUS_UPDATE. Kept apart from the channels themselves, so a
 * CHANNEL_UPDATE (which carries no status) never wipes one.
 */
import {
  isVoiceChannelType,
  normalizeVoiceStatus,
  type Channel,
  type GatewayDispatch,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { api } from '../lib/api';

interface VoiceStatusState {
  byChannel: Record<Snowflake, string>;
}

export const useVoiceStatus = create<VoiceStatusState>(() => ({ byChannel: {} }));

function fromChannels(channels: readonly Channel[] | undefined): Record<Snowflake, string> {
  const out: Record<Snowflake, string> = {};
  for (const c of channels ?? []) {
    const s = isVoiceChannelType(c.type) ? normalizeVoiceStatus(c.status) : null;
    if (s) out[c.id] = s;
  }
  return out;
}

/** The pure step (tests drive it): the statuses after one gateway dispatch. */
export function nextVoiceStatuses(
  prev: Record<Snowflake, string>,
  frame: GatewayDispatch,
): Record<Snowflake, string> {
  switch (frame.t) {
    case 'READY': {
      const out: Record<Snowflake, string> = {};
      for (const g of frame.d.guilds) Object.assign(out, fromChannels(g.channels));
      return out;
    }
    case 'GUILD_CREATE': {
      const ids = new Set(frame.d.channels.map((c) => c.id));
      const out: Record<Snowflake, string> = {};
      for (const [id, s] of Object.entries(prev)) if (!ids.has(id)) out[id] = s;
      return { ...out, ...fromChannels(frame.d.channels) };
    }
    case 'VOICE_CHANNEL_STATUS_UPDATE': {
      const s = normalizeVoiceStatus(frame.d.status);
      if ((prev[frame.d.id] ?? null) === s) return prev;
      const out = { ...prev };
      if (s) out[frame.d.id] = s;
      else delete out[frame.d.id];
      return out;
    }
    case 'CHANNEL_DELETE': {
      if (!(frame.d.id in prev)) return prev;
      const out = { ...prev };
      delete out[frame.d.id];
      return out;
    }
    default:
      return prev;
  }
}

/** The gateway glue (store/session.ts). */
export function feedVoiceStatus(frame: GatewayDispatch): void {
  const prev = useVoiceStatus.getState().byChannel;
  const next = nextVoiceStatuses(prev, frame);
  if (next !== prev) useVoiceStatus.setState({ byChannel: next });
}

export function useChannelStatus(channelId: Snowflake | null | undefined): string | null {
  return useVoiceStatus((s) => (channelId ? (s.byChannel[channelId] ?? null) : null));
}

/** `PUT /channels/{id}/voice-status`; the gateway's update follows (and is applied here at once). */
export async function setVoiceStatus(
  guildId: Snowflake,
  channelId: Snowflake,
  status: string | null,
): Promise<void> {
  const s = normalizeVoiceStatus(status);
  await api.voice.setStatus(channelId, { status: s });
  feedVoiceStatus({
    op: 0,
    s: 0,
    t: 'VOICE_CHANNEL_STATUS_UPDATE',
    d: { id: channelId, guild_id: guildId, status: s },
  });
}
